/**
 * Reading values out of a terminal workflow run's `output`.
 *
 * The runner does not hand back the workflow's own result directly — it wraps it
 * in an execution envelope:
 *
 * ```json
 * { "engine": "quarkus-flow", "status": "SUCCEEDED", "elapsedMs": 1123,
 *   "instanceId": "WorkflowRun_…", "definitionKey": "create-color-specification",
 *   "output": { "status": "CREATED", "colorSpecificationId": "ColorSpecification_DATA_…" } }
 * ```
 *
 * So whatever the workflow's `summarise` step exported sits one level DOWN, at
 * `run.output.output`. Two separate call sites have already been bitten by this:
 * reading only the outer level found nothing and reported a run that had
 * genuinely SUCCEEDED as a failure (verified against
 * `cpes_record_service_workflowrun` 2026-08-07).
 *
 * The lookup is LEVEL-major, inner first: every candidate key is tried against
 * the inner result before the envelope is consulted at all. That ordering is
 * load-bearing — the envelope carries ids of its own (`instanceId`, sometimes a
 * bare `id`), so a key-major search would match the envelope's run id for any
 * workflow whose result names the record something more specific, and hand back
 * an id no record endpoint resolves.
 */

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

/**
 * The run output's two readable levels, inner (the workflow's own result) first.
 *
 * A run whose output is not an envelope — an older definition, or a workflow
 * that exports a flat object — simply yields one level, so callers need no
 * special case for it.
 */
export const runOutputLevels = (output: unknown): Record<string, unknown>[] => {
  const envelope = asRecord(output);

  if (!envelope) {
    return [];
  }

  const inner = asRecord(envelope.output);

  return inner ? [inner, envelope] : [envelope];
};

/**
 * First non-empty string among `candidateKeys`, searched inner level first.
 *
 * Returns `undefined` rather than an empty string when nothing matches, so
 * callers can distinguish "the workflow named no record" from "it named one and
 * the value was blank" and fall back to a non-record outcome.
 */
export const readRunOutputId = (
  output: unknown,
  candidateKeys: readonly string[],
): string | undefined => {
  for (const level of runOutputLevels(output)) {
    for (const key of candidateKeys) {
      const value = level[key];

      if (typeof value === "string" && value.trim()) {
        return value;
      }
    }
  }

  return undefined;
};

const lowerFirst = (value: string): string =>
  value ? value.charAt(0).toLowerCase() + value.slice(1) : value;

/**
 * The key names a create workflow might use for the record it just made, most
 * specific first: `<camelSchemaName>Pk`, `<camelSchemaName>Id`, then the generic
 * `objectId` / `pk` / `id`.
 *
 * Workflow authors are not consistent here, and the generic keys must come last
 * — they are also the names the runner's envelope uses for the RUN, so a
 * schema-specific key is the only unambiguous match.
 */
export const createdRecordIdKeys = (schemaName: string): string[] => {
  const camel = lowerFirst(schemaName);

  return [`${camel}Pk`, `${camel}Id`, "objectId", "pk", "id"];
};

export type RunMessageSeverity = "error" | "warning" | "info";

export interface RunMessageGroup {
  severity: RunMessageSeverity;
  texts: string[];
}

/**
 * Keys a workflow's own result has been seen (or is reasonably expected) to
 * carry for non-fatal issues, most severe first. This is separate from
 * `run.errorMessage` (rendered by `RunErrorPanel`) — that is the RUN failing
 * outright; these are issues a SUCCEEDED run still wants to surface (a partial
 * failure, a skipped step, an advisory note). Rendered only when present —
 * nothing here is assumed to always exist.
 */
const MESSAGE_KEY_GROUPS: ReadonlyArray<{
  keys: readonly string[];
  severity: RunMessageSeverity;
}> = [
  { keys: ["errors", "error"], severity: "error" },
  { keys: ["warnings", "warning"], severity: "warning" },
  { keys: ["messages", "message"], severity: "info" },
];

/** Every key the message groups claim — so result renderers can set them aside. */
export const RUN_MESSAGE_KEYS: ReadonlySet<string> = new Set(
  MESSAGE_KEY_GROUPS.flatMap((group) => group.keys),
);

/**
 * A string, or an array of strings — the only shapes these fields are known to
 * take. Anything else (a nested object, a number) is left to the raw envelope
 * rather than guessed at.
 */
const toMessageList = (value: unknown): string[] | undefined => {
  if (typeof value === "string" && value.trim()) {
    return [value];
  }

  if (Array.isArray(value)) {
    const strings = value.filter(
      (item): item is string => typeof item === "string" && item.trim().length > 0,
    );

    return strings.length > 0 ? strings : undefined;
  }

  return undefined;
};

/**
 * Collects severity-grouped messages across the run-output levels, inner (the
 * workflow's own result) first — same level-major convention `readRunOutputId`
 * uses for ids, so a key the envelope happens to share does not shadow the real
 * one. Shared by `RunOutputPanel` (full panel) and the run tracker (completion
 * toast) — one parse, two surfaces.
 */
export const collectRunMessageGroups = (output: unknown): RunMessageGroup[] => {
  const levels = runOutputLevels(output);
  const groups: RunMessageGroup[] = [];

  for (const { keys, severity } of MESSAGE_KEY_GROUPS) {
    for (const level of levels) {
      const texts = keys.map((key) => toMessageList(level[key])).find((list) => list);

      if (texts) {
        groups.push({ severity, texts });
        break;
      }
    }
  }

  return groups;
};

/**
 * The first warning a run's output carries, or `undefined`. The completion
 * toast has room for one line, not a panel — so it names the first warning and
 * leaves the rest to the run page.
 */
export const firstRunWarning = (output: unknown): string | undefined =>
  collectRunMessageGroups(output).find((group) => group.severity === "warning")?.texts[0];
